"use client";

import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from "react";
import { ModalContext } from "../hooks/useModal";
import { useProfile } from "../hooks/useProfile";
import { useSession } from "./SupabaseProvider";
import { watchScheduleCheckout } from "../helpers/watchScheduleCheckout";
import { hasActiveConsumerSubscription } from "../helpers/consumerSubscription";

export type ScheduleCheckout = {
  start: (checkoutWindow: Window) => void;
  cancel: (checkoutWindow: Window) => void;
  isCurrent: (checkoutWindow: Window) => boolean;
};
export const ScheduleCheckoutContext = createContext<ScheduleCheckout | null>(null);

const DEFAULT_NOTE = "Checkout opens in a separate tab. Return here after payment to finish your schedule.";

/** Only scheduler paywalls (and Auto post's) opt in; ordinary pricing keeps its usual redirect. */
/** `note={null}` shows no line above the paywall. */
export function ScheduleCheckoutProvider({ children, note = DEFAULT_NOTE }: { children: ReactNode; note?: string | null }) {
  const modal = useContext(ModalContext);
  const session = useSession();
  const { data: profile, refetch } = useProfile();
  const ownerId = session?.user.id;
  const owner = useRef(ownerId);
  const content = modal.children;
  const currentModal = useRef(modal);
  const mounted = useRef(false);
  const checkout = useRef<Window | null>(null);
  const stopWatching = useRef<(() => void) | undefined>(undefined);
  useLayoutEffect(() => { currentModal.current = modal; }, [modal]);
  useLayoutEffect(() => {
    owner.current = ownerId;
    mounted.current = true;
    return () => {
      mounted.current = false;
      checkout.current = null;
      stopWatching.current?.();
    };
  }, [ownerId, content]);

  const value = useMemo<ScheduleCheckout>(() => {
    const isCurrent = (target: Window) => mounted.current && owner.current === ownerId &&
      checkout.current === target && currentModal.current.isOpen && currentModal.current.children === content;
    const cancel = (target: Window) => {
      if (checkout.current !== target) return;
      checkout.current = null;
      stopWatching.current?.();
    };
    return {
      isCurrent,
      cancel,
      start: (target) => {
        stopWatching.current?.();
        checkout.current = target;
        if (!ownerId || !isCurrent(target)) return;
        stopWatching.current = watchScheduleCheckout({
          checkoutWindow: target, ownerId, isCurrent: () => isCurrent(target), refetchProfile: refetch,
          onPaid: () => { cancel(target); currentModal.current.close(); },
        });
      },
    };
  }, [ownerId, refetch, content]);

  useEffect(() => {
    const onFocus = () => {
      const target = checkout.current;
      // Stripe may sever WindowProxy via COOP and report closed early. Focus
      // still verifies payment even when that stopped the bounded poll.
      if (target && ownerId && value.isCurrent(target)) {
        stopWatching.current?.();
        stopWatching.current = watchScheduleCheckout({
          checkoutWindow: target, ownerId, isCurrent: () => value.isCurrent(target), refetchProfile: refetch,
          // One minute covers the webhook arriving shortly after the return.
          ignoreWindowClosed: true, timeoutMs: 60_000,
          onPaid: () => { value.cancel(target); currentModal.current.close(); },
        });
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [ownerId, refetch, value]);

  // Focus refetch also completes recovery after a long checkout outlives the
  // bounded poll window, provided this is still the same owner's paywall.
  useEffect(() => {
    const target = checkout.current;
    if (target && value.isCurrent(target) && profile?.id === ownerId && hasActiveConsumerSubscription(profile)) {
      value.cancel(target);
      modal.close();
    }
  }, [modal, ownerId, profile, value]);

  return (
    <ScheduleCheckoutContext.Provider key={ownerId} value={value}>
      {note ? (
        <p className="text-center text-sm text-slate-500 px-3 pt-3">
          {note}
        </p>
      ) : null}
      {children}
    </ScheduleCheckoutContext.Provider>
  );
}
